import React, { KeyboardEvent, FunctionComponent, useCallback, useState, useEffect } from 'react';

import classnames from 'classnames';
import { isEmpty, round } from 'lodash';
import numeral from 'numeral';
import { Col, Form, Pagination, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { UsePaginationInstanceProps } from 'react-table';

import { useDebouncedFunction } from 'core/hooks/useDebouncedFunction';
import { Select } from 'core/select/Select';

import 'core/table/pagination/TablePagination.scss';

interface Props<D extends Record<string, unknown> = Record<string, unknown>>
    extends Omit<UsePaginationInstanceProps<D>, 'page' | 'setPageSize' | 'pageOptions'> {
    className?: string;
    pageIndex?: number;
    supportsDynamicPageNumber?: boolean;
    showXofY?: boolean;
    rowsPerPage?: number; // current page size
    onRowsPerPageChange?: (pageSize: number) => void; // handler for page size change
    pageLabel?: string; // label for the page size selector
    rowsPerPageOptions?: number[]; // options for rows per page selector
    menuPlacement?: 'auto' | 'top' | 'bottom'; // rows per page selector menu placement
}

export const TablePagination: FunctionComponent<Props> = ({
    canNextPage,
    canPreviousPage,
    className,
    gotoPage,
    nextPage,
    pageCount,
    pageIndex,
    previousPage,
    supportsDynamicPageNumber,
    showXofY = true,
    rowsPerPage = 10, // current page size with default value
    onRowsPerPageChange, // handler for page size change
    pageLabel, // label for the page size selector
    rowsPerPageOptions, // options for rows per page selector,
    menuPlacement = 'auto',
}) => {
    const { t } = useTranslation(['core/table']);

    const [click, setClicked] = useState(false);
    const [dynamicPageNumber, setDynamicPageNumber] = useState(1);

    const { register } = useForm({
        criteriaMode: 'firstError',
        shouldFocusError: true,
        shouldUnregister: true,
    });

    const debouncedGotoPage = useDebouncedFunction(gotoPage, 500);

    const gotoSpecifiedPage = useCallback(
        (pageNumber: string) => {
            if (isEmpty(pageNumber)) {
                setClicked(false);
            } else if (+pageNumber > 0 && +pageNumber <= pageCount) {
                setClicked(true);
                const pageNumberRoundedOff = round(+pageNumber);

                setDynamicPageNumber(+pageNumberRoundedOff);
                debouncedGotoPage(+pageNumberRoundedOff - 1);
            }
        },
        [pageCount, debouncedGotoPage],
    );

    const rejectNonIntegers = (evt: KeyboardEvent<HTMLInputElement>): void => {
        const currentValue = evt.currentTarget.value + evt.key;

        if (
            evt.key === '.' ||
            evt.key === 'e' ||
            evt.key === '+' ||
            evt.key === '-' ||
            (evt.key !== 'Backspace' && currentValue.length > 10)
        )
            evt.preventDefault();
    };

    useEffect(() => setDynamicPageNumber(pageIndex! + 1), [pageIndex]);

    if (!pageCount || pageCount < 2 || typeof pageIndex === 'undefined') {
        return null;
    }

    const options = rowsPerPageOptions?.map((i: number) => ({
        // Prepare options for rows per page selector
        value: i,
        label: `${i}`,
    }));

    const currentPage = pageIndex + 1;
    const currentPageFormatted = numeral(currentPage).format('0,0');
    const pageCountFormatted = numeral(pageCount).format('0,0');
    const selected = options?.find((option) => option.value === rowsPerPage);

    const onChange = (pageSize: number) => {
        if (onRowsPerPageChange) {
            onRowsPerPageChange(pageSize); // Notify parent component of page size change
        }
    };

    return (
        <Row className={classnames('r4e-table-pagination justify-content-end align-items-center', className)}>
            {/* Render rows per page selector if handler is provided */}
            {onRowsPerPageChange && (
                <>
                    {/* Render page label if provided */}
                    <Col>{pageLabel}</Col>
                    <Col>
                        {/* Render Select component for rows per page selection */}
                        <Select
                            className="page-size-selector"
                            value={selected}
                            default={selected}
                            options={options}
                            onChange={(selectedOption: any): void => onChange(selectedOption.value)}
                            menuPlacement={menuPlacement}
                        />
                    </Col>
                </>
            )}
            <Col sm={{ order: 1, span: 'auto' }} className="pl-sm-0">
                <Pagination className="m-0">
                    <Pagination.First
                        onClick={(): void => gotoPage(0)}
                        disabled={!canPreviousPage}
                        title={t('PAGINATION_FIRST_PAGE_TEXT')}
                    >
                        <i className="icon-angle-double-left" />
                        <span className="sr-only">{t('PAGINATION_FIRST_PAGE_TEXT')}</span>
                    </Pagination.First>
                    <Pagination.Item
                        onClick={(): void => previousPage()}
                        disabled={!canPreviousPage}
                        title={t('PAGINATION_PREVIOUS_PAGE_TEXT')}
                    >
                        <i className="icon-angle-left" />
                        <span className="sr-only">{t('PAGINATION_PREVIOUS_PAGE_TEXT')}</span>
                    </Pagination.Item>
                    {supportsDynamicPageNumber && (
                        <Form
                            className="pagination-form"
                            onSubmit={(event: React.ChangeEvent<HTMLFormElement>): void => {
                                event.preventDefault();
                            }}
                        >
                            <Form.Control
                                ref={register({
                                    required: true,
                                    valueAsNumber: true,
                                    validate: {
                                        inRange: (pageNumber: string) => +pageNumber <= pageCount && +pageNumber >= 0,
                                    },
                                })}
                                name="pagination"
                                type="number"
                                onChange={(evt: React.ChangeEvent<HTMLInputElement>): void => {
                                    evt.preventDefault();
                                    gotoSpecifiedPage(evt.target.value);
                                }}
                                onClick={(): void => setClicked(true)}
                                onKeyDown={rejectNonIntegers}
                                className="pagination-control"
                                value={click ? dynamicPageNumber : ''}
                                autoComplete="off"
                            />

                            {!click && (
                                <span className="position-icon" role="button" tabIndex={0}>
                                    <i className="icon-search" />
                                </span>
                            )}
                        </Form>
                    )}
                    <Pagination.Item
                        onClick={(): void => nextPage()}
                        disabled={!canNextPage}
                        title={t('PAGINATION_NEXT_PAGE_TEXT')}
                    >
                        <i className="icon-angle-right" />
                        <span className="sr-only">{t('PAGINATION_NEXT_PAGE_TEXT')}</span>
                    </Pagination.Item>
                    <Pagination.Last
                        onClick={(): void => gotoPage(pageCount - 1)}
                        disabled={!canNextPage}
                        title={t('PAGINATION_LAST_PAGE_TEXT')}
                    >
                        <i className="icon-angle-double-right" />
                        <span className="sr-only">{t('PAGINATION_LAST_PAGE_TEXT')}</span>
                    </Pagination.Last>
                </Pagination>
            </Col>
            {showXofY ? (
                <Col
                    sm={{ order: 0, span: 'auto' }}
                    className="mt-2 mt-sm-0 pr-sm-2"
                    title={t('PAGINATION_X_OF_Y_TOOLTIP', {
                        paginationX: currentPageFormatted,
                        paginationY: pageCountFormatted,
                    })}
                >
                    {t('PAGINATION_X_OF_Y_TEXT', {
                        paginationX: numeral(currentPage).format('0,0'),
                        paginationY: pageCountFormatted,
                    })}
                </Col>
            ) : (
                <></>
            )}
        </Row>
    );
};

TablePagination.defaultProps = {
    pageIndex: 0,
};
